import React, { useEffect, useMemo, useState } from "react";
import PermissionButton from "../../shared/PermissionButton";
import Button from "../../shared/Button";
import {
  useEducationLevelByAssessmentModule,
  useEducationLevelDivisions,
  useYearLevelByEducationLevel,
} from "../../../hooks/useAssessment";
import {
  useSelectClassrooms,
  useStudentOptionalSubjects,
  useStudents,
  useSubjectsByYearLevel,
} from "../../../hooks/useOptionalSubjects";
import { Spinner } from "reactstrap";
import swal from "sweetalert";
import Select from "../../shared/Select";

const AssignOptionalSubject = ({ name }) => {
  const { educationLevel } = useEducationLevelByAssessmentModule();
  const { educationDivision, getEducationLevelDivision } =
    useEducationLevelDivisions();
  const { yearLevel, getYearLevelByEducationLevel } =
    useYearLevelByEducationLevel();
  const { classrooms, getClassrooms } = useSelectClassrooms();
  const { subjects, getSubjectsByYearLevel } = useSubjectsByYearLevel();
  const {
    students,
    setStudents,
    getAllStudents,
    loading: isStudentsLoading,
  } = useStudents();
  const { loading, insertStudentClassroomOptionalSubject } =
    useStudentOptionalSubjects();

  const [selectAll, setSelectAll] = useState(false);
  const [selectRow, setSelectRow] = useState({});

  const [formData, setFormData] = useState({
    educationLevelId: "",
    term: "",
    class: "",
    section: "",
    optionalSubject: "",
  });

  const educationLevelDivisionOptions = useMemo(() => {
    if (educationDivision?.length === 0) return [];
    return [{ label: "All", value: "" }, ...educationDivision];
  }, [educationDivision]);

  const yearLevelOption = useMemo(() => {
    if (!yearLevel) return;
    return yearLevel?.map((item) => ({ label: item.name, value: item.id }));
  }, [yearLevel]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    const updated = {};
    students.forEach((item) => {
      updated[item?.id] = checked;
    });

    setSelectRow(updated);
  };

  const handleRowSelect = (item, checked) => {
    const updated = { ...selectRow, [item]: checked };
    setSelectRow(updated);

    const allChecked = students.every((student) => updated[student] === true);

    setSelectAll(allChecked);
  };

  const handleClassSelect = (option) => {
    setFormData((prev) => ({ ...prev, class: option?.value, section: "", optionalSubject: "", }));
    setStudents([]);
    getClassrooms(option?.value);

    const params = {
      educationLevelDivisionId: formData.term,
      educationLevelId: formData.educationLevelId,
      isCompulsory: false,
      yearLevelId: option?.value,
    };

    getSubjectsByYearLevel(params);
  };

  const handleAssignOptionalSubject = async () => {
    const students = Object.entries(selectRow)
      .filter(([, value]) => value === true)
      .map(([key]) => ({ admissionId: Number(key) }));

    if (students.length === 0) {
      swal("Oops", "Please select at least one student");
      return;
    }

    if (formData.optionalSubject === "") {
      swal("Oops", "Please select an optional subject");
      return;
    }

    const payload = {
      students: students,
      classroomId: formData.section,
      yearLevelId: formData.class,
      subjectId: formData.optionalSubject,
      educationLevelId: formData.educationLevelId,
      educationLevelDivisionId: formData.term === "" ? null : formData.term,
    };

    try {
      await insertStudentClassroomOptionalSubject(payload);
    } catch (error) {
      console.error("Insert Failed: ", error);
    }

    setSelectRow({});
    setSelectAll(false);
  };

  useEffect(() => {
    if (educationLevel?.length > 0) {
      const educationLevelId = educationLevel[0].value;
      setFormData((prev) => ({ ...prev, educationLevelId: educationLevelId }));
      getEducationLevelDivision(educationLevelId);
      getYearLevelByEducationLevel(educationLevelId);
    }
  }, [educationLevel]);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>Assign Student Optional Subject</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Subjects Setup"}
              widgetName={name}
              header={"Assign Student Optional Subject"}
              activityName={"insert-student-optional-subjects"}
            />
          </div>
        </div>

        <form className="arr-form grid">
          <Select
            name={"class"}
            value={
              yearLevelOption?.find((opt) => opt?.value === formData?.class) ||
              null
            }
            options={yearLevelOption}
            onChange={(option) => handleClassSelect(option)}
            placeholder={"Select Class"}
            isClearable={false}
          />

          <Select
            name={"section"}
            value={
              classrooms?.find((opt) => opt?.value === formData?.section) ||
              null
            }
            options={classrooms}
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, section: option?.value }));

              const params = {
                yearLevelId: formData.class,
                classroomId: option?.value,
              };

              getAllStudents(params);
            }}
            isDisabled={!formData.class}
            placeholder={"Select Section"}
            isClearable={false}
          />

          <Select
            name={"educationDivision"}
            value={
              educationLevelDivisionOptions?.find(
                (opt) => opt?.value === formData?.term,
              ) || null
            }
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, term: option?.value }));
            }}
            options={educationLevelDivisionOptions}
            placeholder={"Select Term"}
            isClearable={false}
          />

          <Select
            name={"subjects"}
            value={
              subjects?.find(
                (opt) => opt?.value === formData?.optionalSubject,
              ) || null
            }
            options={subjects}
            onChange={(option) =>
              setFormData((prev) => ({
                ...prev,
                optionalSubject: option?.value,
              }))
            }
            isDisabled={!formData?.class}
            placeholder={"Select Optional Subject"}
            isClearable={false}
          />
        </form>

        <div className="action-btns end" style={{ marginTop: "32px" }}>
          <Button
            disabled={loading.insert}
            onClick={handleAssignOptionalSubject}
          >
            {loading.insert ? (
              <Spinner size={"sm"} color="white" />
            ) : (
              "Assign Optional Subject"
            )}
          </Button>
        </div>

        <div className="arr-table-container">
          <table aria-label="student-table" className="arr-table">
            <thead>
              <tr>
                <th>
                  <input
                    aria-label="selectAllCheckbox"
                    style={{ accentColor: "#126696" }}
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Student Name</th>
                <th>Roll Number</th>
                <th>Gender</th>
              </tr>
            </thead>

            <tbody>
              {isStudentsLoading ? (
                <tr>
                  <td colSpan={4}>
                    <div className="arr-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              ) : students?.length > 0 ? (
                students.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        aria-label="selectStudent"
                        style={{ accentColor: "#126696" }}
                        type="checkbox"
                        checked={selectRow[item?.id] || false}
                        onChange={(e) =>
                          handleRowSelect(item?.id, e.target.checked)
                        }
                      />
                    </td>

                    <td>{item?.name ?? "N/A"}</td>
                    <td>{item?.rollNumber ?? "N/A"}</td>
                    <td>{item?.gender ?? "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AssignOptionalSubject;
